import {
  renderModalActionsFirstStep,
  renderModalActionsMiddleStep,
} from "components/assignmentModals/contentAssignmentModal/utils/contentAssignmentModalActions";
import { type RenderActions, type RenderModalActionsType, type StepsOptions } from "components/assignmentModals/types";
import { renderSteps } from "components/assignmentModals/utils/renderSteps";
import ModalWithSteps from "components/modal/ModalWithSteps";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { isEmpty } from "lodash";
import { type FC, useMemo, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import AddPacksModalStep from "./AddPacksModalStep";
import { type PackMap } from "components/assignmentModals/groupTemplatePacksConfirmationModal/types";
import { PacksLicensingModalStep } from "./PacksLicensingModalStep";
import { bindAction } from "interfaces";
import { savePacks } from "../../state/thunks/TemplateAvailablePacksThunk";
import { reset } from "../../state/TemplateAvailablePacksSlice";
import cn from "classnames";
import styles from "./addPacksModal.module.scss";
import { renderModalActionsLastStep } from "../utils/renderModalActionsLastStep";
import { addDomainToAccount } from "../../../../Accounts/state/thunks/accountDomainsThunk";
import { reset as resetCustomers } from "../../state/TemplateAvailableCustomersSlice";
import { reset as resetAvailableDomains } from "features/Accounts/state/slices/accountDomainsSlice";

export interface AddPacksModalProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  templateId: number;
}

export type AddPacksModalPropsAll = PropsFromRedux & AddPacksModalProps;

export const AddPacksModal: FC<AddPacksModalPropsAll> = ({
  showModal,
  setShowModal,
  selectedIds,
  templateId,
  addPacksToGroupTemplate,
  addDomainToAccountsAction,
  customers,
  resetCustomers,
  resetAvailableDomainsAction,
  reset,
}) => {
  const selectedPacks = useRef<PackMap>(new Map());
  const [isDisabled, setIsDisabled] = useState(true);
  const renderPacksStep = (renderModalActions: RenderModalActionsType) => (
    <AddPacksModalStep
      templateId={templateId}
      packMap={selectedPacks.current}
      header="Add Packs"
      renderModalActions={renderModalActions}
    />
  );
  const renderPackAccountsStep = (renderModalActions: RenderModalActionsType) => (
    <PacksLicensingModalStep
      packMap={selectedPacks.current}
      header="Provision Licenses"
      setIsDisabled={setIsDisabled}
      renderModalActions={renderModalActions}
    />
  );

  const onModalClose = () => {
    setShowModal(false);
    selectedPacks.current.clear();
    resetCustomers();
    resetAvailableDomainsAction();
    reset();
  };

  const customerIds = useMemo(() => customers.map((customer) => customer.id), [customers]);

  const stepsOptions: StepsOptions[] = [
    {
      renderStep: renderPacksStep,
      modalActionConfig: {
        previous: {
          onClick: onModalClose,
        },
        next: {
          disabled: isEmpty(selectedIds),
        },
        confirm: {
          disabled: isEmpty(selectedIds),
          onClick: async () => {
            await addPacksToGroupTemplate(templateId, [...selectedPacks.current.values()], customerIds);
            onModalClose();
          },
        },
      },
    },
    {
      renderStep: renderPackAccountsStep,
      skipStep: customerIds.length === 0,
      modalActionConfig: {
        confirm: {
          disabled: isDisabled,
          onClick: async () => {
            await addPacksToGroupTemplate(templateId, [...selectedPacks.current.values()], customerIds);
            await addDomainToAccountsAction(customerIds);
            onModalClose();
          },
        },
      },
    },
  ];

  const renderActions: RenderActions = {
    renderModalActionsFirstStep,
    renderModalActionsLastStep,
    renderModalActionsMiddleStep,
  };

  return (
    <ModalWithSteps
      className={cn(styles.root, { [styles["multiple-packs"]]: selectedIds.length > 2 })}
      showModal={showModal}
      onCancel={onModalClose}
      scrolling
    >
      {renderSteps(stepsOptions, renderActions)}
    </ModalWithSteps>
  );
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  addPacksToGroupTemplate: bindAction(savePacks, dispatch),
  addDomainToAccountsAction: bindAction(addDomainToAccount, dispatch),
  resetCustomers: () => dispatch(resetCustomers()),
  resetAvailableDomainsAction: bindAction(resetAvailableDomains, dispatch),
  reset: () => dispatch(reset()),
});

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    selectedIds: state.people.groupTemplate.availablePacks.packsToAdd,
    customers: state.people.groupTemplate.customers.items,
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AddPacksModal);
export default ConnectedComponent;
